/**
 * Everything a game page says about a game that is not its name, its picture or its prose.
 *
 * A store page answers a short list of questions, and they are the same list on every store:
 * who made it, when it came out, what sort of game it is, how many people can play it and at
 * which desk, what it needs to run, and what it is spoken in. Steam's page is the one everybody
 * has already read, so the questions here are Steam's questions, in Steam's words — a game
 * imported from a Steam page loses nothing, and a player arriving from one is not learning a
 * new layout to find the same fact.
 *
 * Every one of them is a table rather than a column of enumerated cases, for the reason the
 * artwork and storefront tables give: the admin form labels itself from the table, the API
 * validates against the table, and the game page draws from the table, so a new feature or a
 * new language is a row here and nothing else anywhere.
 *
 * The free-text parts — the requirement lines, the genres, the legal notice — are stored as the
 * admin typed them and never parsed. What counts as a graphics card is not a thing a schema
 * knows, and a form that refuses "anything from the last ten years" is a form that gets fought
 * rather than filled in.
 */

import { z } from 'zod';

/* ---------------------------------------------------------------- features */

/**
 * How a feature is grouped on the page.
 *
 * Steam draws one flat list, which is why finding out whether a game has online co-op means
 * reading past eleven things about cloud saves. The group is what lets the page lead with the
 * answer somebody came for — who can play, and with whom — and put the platform plumbing
 * underneath it.
 */
export const FEATURE_GROUPS = ['players', 'together', 'platform'] as const;
export type FeatureGroup = (typeof FEATURE_GROUPS)[number];

export const FEATURE_GROUP_LABELS: Record<FeatureGroup, string> = {
  players: 'Players',
  together: 'Playing together',
  platform: 'Features',
};

export interface GameFeatureSpec {
  id: GameFeatureId;
  /** Steam's own wording, so the two pages read as the same claim. */
  label: string;
  group: FeatureGroup;
  /** The line under it in the admin picker, where the difference between two rows is subtle. */
  hint: string;
}

/**
 * The feature list, in the order a page reads it.
 *
 * Order matters twice over: it is the order of the checkboxes in the admin and the order of the
 * chips on the game page, and the two being the same is what makes a list of twenty possible
 * things quick to fill in and quick to scan.
 */
export const GAME_FEATURES = [
  {
    id: 'single_player',
    label: 'Single-player',
    group: 'players',
    hint: 'Playable alone, start to finish.',
  },
  {
    id: 'multi_player',
    label: 'Multi-player',
    group: 'players',
    hint: 'More than one player, in any form. Tick this alongside whichever kinds apply.',
  },
  {
    id: 'co_op',
    label: 'Co-op',
    group: 'together',
    hint: 'Players on the same side.',
  },
  {
    id: 'online_co_op',
    label: 'Online co-op',
    group: 'together',
    hint: 'Co-op over the internet.',
  },
  {
    id: 'lan_co_op',
    label: 'LAN co-op',
    group: 'together',
    hint: 'Co-op over a local network.',
  },
  {
    id: 'shared_screen_co_op',
    label: 'Shared or split screen co-op',
    group: 'together',
    hint: 'Co-op on one screen, at one desk or one sofa.',
  },
  {
    id: 'pvp',
    label: 'PvP',
    group: 'together',
    hint: 'Players against each other.',
  },
  {
    id: 'online_pvp',
    label: 'Online PvP',
    group: 'together',
    hint: 'PvP over the internet.',
  },
  {
    id: 'shared_screen_pvp',
    label: 'Shared or split screen PvP',
    group: 'together',
    hint: 'PvP on one screen.',
  },
  {
    id: 'cross_platform',
    label: 'Cross-platform play',
    group: 'together',
    hint: 'Players on different devices are in the same session.',
  },
  {
    id: 'controller_full',
    label: 'Full controller support',
    group: 'platform',
    hint: 'Every screen, menus included, works from a pad.',
  },
  {
    id: 'controller_partial',
    label: 'Partial controller support',
    group: 'platform',
    hint: 'Playable with a pad, but something still needs a mouse or a keyboard.',
  },
  {
    id: 'achievements',
    label: 'Achievements',
    group: 'platform',
    hint: 'The game tracks and awards them.',
  },
  {
    id: 'cloud_saves',
    label: 'Cloud saves',
    group: 'platform',
    hint: 'Saves follow the player to another machine.',
  },
  {
    id: 'captions',
    label: 'Subtitles and captions',
    group: 'platform',
    hint: 'Speech, and the sounds that matter, written on screen.',
  },
  {
    id: 'colourblind',
    label: 'Colourblind options',
    group: 'platform',
    hint: 'Nothing the player has to tell apart is told apart by colour alone.',
  },
  {
    id: 'mod_support',
    label: 'Mods or level editor',
    group: 'platform',
    hint: 'Ships with a way to make things for it.',
  },
  {
    id: 'hdr',
    label: 'HDR',
    group: 'platform',
    hint: 'Renders in high dynamic range on a display that takes it.',
  },
  {
    id: 'vr',
    label: 'VR supported',
    group: 'platform',
    hint: 'Playable in a headset. Not the same as VR only.',
  },
] as const satisfies readonly { id: string; label: string; group: FeatureGroup; hint: string }[];

export type GameFeatureId = (typeof GAME_FEATURES)[number]['id'];

export const GAME_FEATURE_IDS = GAME_FEATURES.map((f) => f.id) as readonly GameFeatureId[];

export const gameFeatureIdSchema = z.enum(
  GAME_FEATURE_IDS as unknown as [GameFeatureId, ...GameFeatureId[]],
);

export const FEATURE_BY_ID: Record<GameFeatureId, GameFeatureSpec> = Object.fromEntries(
  GAME_FEATURES.map((spec) => [spec.id, spec]),
) as Record<GameFeatureId, GameFeatureSpec>;

/**
 * A game's features, de-duplicated and put back into table order.
 *
 * The order the boxes were ticked in is not an order anybody meant, and it would make two games
 * with the same features draw two different lists. Sorting here rather than at the point of
 * drawing means the column already holds the order the page wants.
 */
export const gameFeaturesSchema = z
  .array(gameFeatureIdSchema)
  .max(GAME_FEATURE_IDS.length)
  .transform((ids) => GAME_FEATURE_IDS.filter((id) => ids.includes(id)));

/** The features a game has, grouped the way the page draws them. Empty groups are dropped. */
export function featuresByGroup(
  ids: readonly GameFeatureId[],
): { group: FeatureGroup; label: string; features: GameFeatureSpec[] }[] {
  return FEATURE_GROUPS.map((group) => ({
    group,
    label: FEATURE_GROUP_LABELS[group],
    features: GAME_FEATURES.filter((f) => f.group === group && ids.includes(f.id)),
  })).filter((section) => section.features.length > 0);
}

/* ------------------------------------------------------------ requirements */

/**
 * The devices requirements are written for.
 *
 * Not `PLATFORMS`, which is the list of things we build binaries for. Linux has no build of its
 * own — it runs the Windows one through Proton — and it still needs its own requirements,
 * because "does it run on a Steam Deck" is exactly the question that sends somebody looking for
 * this box, and the Windows numbers do not answer it.
 */
export const REQUIREMENT_PLATFORMS = ['windows', 'linux', 'android'] as const;
export const requirementPlatformSchema = z.enum(REQUIREMENT_PLATFORMS);
export type RequirementPlatform = z.infer<typeof requirementPlatformSchema>;

export const REQUIREMENT_PLATFORM_LABELS: Record<RequirementPlatform, string> = {
  windows: 'Windows',
  linux: 'Linux and Steam Deck',
  android: 'Android',
};

export const REQUIREMENT_TIERS = ['minimum', 'recommended'] as const;
export const requirementTierSchema = z.enum(REQUIREMENT_TIERS);
export type RequirementTier = z.infer<typeof requirementTierSchema>;

export const REQUIREMENT_TIER_LABELS: Record<RequirementTier, string> = {
  minimum: 'Minimum',
  recommended: 'Recommended',
};

export interface RequirementFieldSpec {
  id: RequirementFieldId;
  label: string;
  /** An example of the sort of answer this line takes, shown in the empty field. */
  placeholder: string;
  /**
   * The devices this line is asked about. A DirectX version is a Windows question and nothing
   * else, and asking it three times over is how a form teaches people to skip fields.
   */
  platforms?: readonly RequirementPlatform[];
}

export const REQUIREMENT_FIELDS = [
  {
    id: 'os',
    label: 'OS',
    placeholder: 'Windows 10 64-bit',
  },
  {
    id: 'processor',
    label: 'Processor',
    placeholder: 'Intel Core i5-6600K / AMD Ryzen 5 1600',
  },
  {
    id: 'memory',
    label: 'Memory',
    placeholder: '8 GB RAM',
  },
  {
    id: 'graphics',
    label: 'Graphics',
    placeholder: 'NVIDIA GTX 1060 6 GB / AMD RX 580 8 GB',
  },
  {
    id: 'directx',
    label: 'DirectX',
    placeholder: 'Version 12',
    platforms: ['windows'],
  },
  {
    id: 'storage',
    label: 'Storage',
    placeholder: '20 GB available space',
  },
  {
    id: 'sound',
    label: 'Sound card',
    placeholder: 'DirectX compatible',
    platforms: ['windows', 'linux'],
  },
  {
    id: 'network',
    label: 'Network',
    placeholder: 'Broadband internet connection',
  },
  {
    id: 'notes',
    label: 'Additional notes',
    placeholder: 'A controller is recommended.',
  },
] as const satisfies readonly {
  id: string;
  label: string;
  placeholder: string;
  platforms?: readonly RequirementPlatform[];
}[];

export type RequirementFieldId = (typeof REQUIREMENT_FIELDS)[number]['id'];

/** The lines worth asking about this device, in the order a requirements box lists them. */
export function requirementFieldsFor(
  platform: RequirementPlatform,
): readonly RequirementFieldSpec[] {
  // Widened first. Read as written, `platforms` is a union of literal tuples, and `includes` on
  // a union of tuples takes only what all of them have in common — which here is `"windows"`.
  return REQUIREMENT_FIELD_SPECS.filter(
    (field) => !field.platforms || field.platforms.includes(platform),
  );
}

/** The same table, typed as the interface rather than as its own literals. */
export const REQUIREMENT_FIELD_SPECS: readonly RequirementFieldSpec[] = REQUIREMENT_FIELDS;

/** One box: the lines somebody filled in, and only those. */
export type RequirementSpec = Partial<Record<RequirementFieldId, string>>;

/**
 * Every requirements box on a game.
 *
 * Partial at all three levels, because most games have exactly one: a Windows minimum and
 * nothing else. An empty object is the normal state of this column and draws nothing.
 */
export type SystemRequirements = Partial<
  Record<RequirementPlatform, Partial<Record<RequirementTier, RequirementSpec>>>
>;

const requirementSpecSchema = z
  .object(
    Object.fromEntries(
      REQUIREMENT_FIELDS.map((field) => [field.id, z.string().max(400)]),
    ) as Record<RequirementFieldId, z.ZodString>,
  )
  .partial();

const requirementTiersSchema = z
  .object(
    Object.fromEntries(REQUIREMENT_TIERS.map((tier) => [tier, requirementSpecSchema])) as Record<
      RequirementTier,
      typeof requirementSpecSchema
    >,
  )
  .partial();

/**
 * The requirements as they are stored.
 *
 * `normalizeRequirements` runs as part of parsing, so a box whose every line was cleared does
 * not survive as an empty object the page would then draw an empty heading for.
 */
export const systemRequirementsSchema = z
  .object(
    Object.fromEntries(
      REQUIREMENT_PLATFORMS.map((platform) => [platform, requirementTiersSchema]),
    ) as Record<RequirementPlatform, typeof requirementTiersSchema>,
  )
  .partial()
  .transform(normalizeRequirements);

/**
 * Requirements with the blanks taken out.
 *
 * A form hands back every line it drew, most of them empty, and storing those would mean the
 * difference between "no Linux requirements" and "a Linux box somebody opened and closed again"
 * is something the page has to test for. Emptiness is resolved once, here.
 */
export function normalizeRequirements(raw: SystemRequirements): SystemRequirements {
  const out: SystemRequirements = {};
  for (const platform of REQUIREMENT_PLATFORMS) {
    const tiers = raw[platform];
    if (!tiers) continue;
    const kept: Partial<Record<RequirementTier, RequirementSpec>> = {};
    for (const tier of REQUIREMENT_TIERS) {
      const spec = tiers[tier];
      if (!spec) continue;
      const lines: RequirementSpec = {};
      for (const field of REQUIREMENT_FIELDS) {
        const value = spec[field.id]?.trim();
        if (value) lines[field.id] = value;
      }
      if (Object.keys(lines).length > 0) kept[tier] = lines;
    }
    if (Object.keys(kept).length > 0) out[platform] = kept;
  }
  return out;
}

/** True when there is a single line anywhere in here worth drawing a section for. */
export function hasRequirements(reqs: SystemRequirements): boolean {
  return REQUIREMENT_PLATFORMS.some((platform) => Object.keys(reqs[platform] ?? {}).length > 0);
}

/** The devices this game has said anything about, in table order. */
export function requirementPlatformsIn(reqs: SystemRequirements): RequirementPlatform[] {
  return REQUIREMENT_PLATFORMS.filter((platform) => Object.keys(reqs[platform] ?? {}).length > 0);
}

/**
 * The requirements column, read.
 *
 * The column is JSON written by this schema, so it parses. It is still read through the schema
 * rather than cast, because a row written before a field existed is a shape this version has
 * never seen, and a game page is not the place to find that out.
 */
export function parseSystemRequirements(raw: string | null | undefined): SystemRequirements {
  if (!raw) return {};
  try {
    const parsed = systemRequirementsSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}

/* -------------------------------------------------------------- languages */

/**
 * What a language is offered as.
 *
 * Steam's three columns, and they are three because they answer three different questions: a
 * game can be menued in a language it is not spoken in, and subtitled in one it is neither.
 */
export const gameLanguageSchema = z.object({
  /** A BCP 47 tag, lowercased. The label comes from the table, not from the column. */
  code: z.string().trim().min(2).max(12),
  /** Menus, HUD, and everything else written by the game itself. */
  interface: z.boolean().default(false),
  /** Spoken lines. */
  audio: z.boolean().default(false),
  /** Written lines over spoken ones. */
  subtitles: z.boolean().default(false),
});
export type GameLanguage = z.infer<typeof gameLanguageSchema>;

export const MAX_LANGUAGES = 40;

/**
 * Languages, de-duplicated by code and with the ones offering nothing dropped.
 *
 * A row with all three boxes clear says a language is not supported, which is what leaving it
 * off the list already says. So it comes off, rather than being stored and filtered later by
 * everything that reads the column.
 */
export const gameLanguagesSchema = z
  .array(gameLanguageSchema)
  .max(MAX_LANGUAGES)
  .transform((rows) => {
    const seen = new Set<string>();
    const out: GameLanguage[] = [];
    for (const row of rows) {
      const code = row.code.trim().toLowerCase();
      if (!code || seen.has(code)) continue;
      if (!row.interface && !row.audio && !row.subtitles) continue;
      seen.add(code);
      out.push({ ...row, code });
    }
    return out;
  });

export interface LanguageSpec {
  code: string;
  /** In English, because the rest of the page is. */
  label: string;
  /** In the language itself, which is how somebody looking for their own finds it. */
  native: string;
}

/**
 * The languages the picker offers.
 *
 * Steam's list, which is the one every localisation house quotes against. It is a suggestion and
 * not a constraint: the column takes any tag, so a game translated into something not on this
 * list is typed in rather than argued with.
 */
export const GAME_LANGUAGES: readonly LanguageSpec[] = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'de', label: 'German', native: 'Deutsch' },
  { code: 'es', label: 'Spanish (Spain)', native: 'Español' },
  { code: 'es-419', label: 'Spanish (Latin America)', native: 'Español de Latinoamérica' },
  { code: 'it', label: 'Italian', native: 'Italiano' },
  { code: 'pt-br', label: 'Portuguese (Brazil)', native: 'Português do Brasil' },
  { code: 'pt', label: 'Portuguese (Portugal)', native: 'Português' },
  { code: 'nl', label: 'Dutch', native: 'Nederlands' },
  { code: 'pl', label: 'Polish', native: 'Polski' },
  { code: 'ru', label: 'Russian', native: 'Русский' },
  { code: 'uk', label: 'Ukrainian', native: 'Українська' },
  { code: 'cs', label: 'Czech', native: 'Čeština' },
  { code: 'hu', label: 'Hungarian', native: 'Magyar' },
  { code: 'ro', label: 'Romanian', native: 'Română' },
  { code: 'tr', label: 'Turkish', native: 'Türkçe' },
  { code: 'sv', label: 'Swedish', native: 'Svenska' },
  { code: 'no', label: 'Norwegian', native: 'Norsk' },
  { code: 'da', label: 'Danish', native: 'Dansk' },
  { code: 'fi', label: 'Finnish', native: 'Suomi' },
  { code: 'el', label: 'Greek', native: 'Ελληνικά' },
  { code: 'bg', label: 'Bulgarian', native: 'Български' },
  { code: 'ja', label: 'Japanese', native: '日本語' },
  { code: 'ko', label: 'Korean', native: '한국어' },
  { code: 'zh-hans', label: 'Simplified Chinese', native: '简体中文' },
  { code: 'zh-hant', label: 'Traditional Chinese', native: '繁體中文' },
  { code: 'th', label: 'Thai', native: 'ไทย' },
  { code: 'vi', label: 'Vietnamese', native: 'Tiếng Việt' },
  { code: 'id', label: 'Indonesian', native: 'Bahasa Indonesia' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
];

const LANGUAGE_BY_CODE = new Map(GAME_LANGUAGES.map((spec) => [spec.code, spec]));

/**
 * What to call a language code on the page.
 *
 * A code that is not in the table is drawn as the code, uppercased. That is not pretty, and it
 * beats dropping a language a game genuinely ships in because our table is short.
 */
export function languageLabel(code: string): string {
  return LANGUAGE_BY_CODE.get(code.trim().toLowerCase())?.label ?? code.toUpperCase();
}

/** The languages column, read. Anything unparseable is no languages rather than a broken page. */
export function parseLanguages(raw: string | null | undefined): GameLanguage[] {
  if (!raw) return [];
  try {
    const parsed = gameLanguagesSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : [];
  } catch {
    return [];
  }
}

/* ----------------------------------------------------------------- genres */

export const MAX_GENRES = 12;

/**
 * The words a game is filed under.
 *
 * Free text rather than a fixed list, because a genre list is a thing a store spends years
 * arguing about and ours would be wrong on the day a game arrived that did not fit it. They are
 * trimmed, de-duplicated case-insensitively and capped, which is all a tag needs.
 */
export const gameGenresSchema = z
  .array(z.string().trim().min(1).max(40))
  .max(MAX_GENRES)
  .transform((tags) => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const tag of tags) {
      const key = tag.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(tag);
    }
    return out;
  });

/** The genres offered as one-tap suggestions in the admin. Typing something else is expected. */
export const SUGGESTED_GENRES: readonly string[] = [
  'Action',
  'Adventure',
  'Platformer',
  'Roguelike',
  'RPG',
  'Shooter',
  'Strategy',
  'Simulation',
  'Puzzle',
  'Racing',
  'Sports',
  'Horror',
  'Survival',
  'Sandbox',
  'Metroidvania',
  'Card game',
  'Rhythm',
  'Visual novel',
];

/** A JSON array of plain strings, read. Used for the genres column. */
export function parseStringList(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** The features column, read. Anything that is no longer a feature is dropped on the way out. */
export function parseFeatures(raw: string | null | undefined): GameFeatureId[] {
  const ids = new Set(parseStringList(raw));
  return GAME_FEATURE_IDS.filter((id) => ids.has(id));
}

/* ------------------------------------------------------------ release date */

/**
 * When it came out, or comes out.
 *
 * `YYYY`, `YYYY-MM` or `YYYY-MM-DD`, because that is the real range of how precisely a release
 * date is ever known: a game announced for next year has a year, one in a calendar has a month,
 * and one with a build already uploaded has a day. A timestamp would force the first two to
 * pretend to a precision nobody has, and the page would say "1 January" about a game that is
 * out some time in 2027.
 */
export const RELEASE_DATE_RULE = /^\d{4}(-\d{2}(-\d{2})?)?$/;

/** What is wrong with a release date, in the words the field shows. Null means it is fine. */
export function releaseDateProblem(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  if (value === '') return null;
  if (!RELEASE_DATE_RULE.test(value)) return 'A release date is 2027, 2027-03 or 2027-03-14.';

  // A shape that parses is not the same as a date that exists. `2027-13` and `2027-02-31` both
  // pass the pattern and neither is a day anybody can wait for.
  const [year, month, day] = value.split('-').map(Number);
  if (month !== undefined && (month < 1 || month > 12)) return 'That month does not exist.';
  if (day !== undefined) {
    const date = new Date(Date.UTC(year!, month! - 1, day));
    if (date.getUTCMonth() !== month! - 1 || date.getUTCDate() !== day) {
      return 'That day is not in that month.';
    }
  }
  return null;
}

/**
 * A zod field for a release date.
 *
 * `superRefine` rather than `refine`, the same way `storeUrlSchema` does it, so the sentence the
 * form already shows is the sentence that comes back if a save somehow gets past it.
 */
export const releaseDateSchema = z
  .string()
  .max(10)
  .nullable()
  .optional()
  .transform((value) => {
    const trimmed = (value ?? '').trim();
    return trimmed === '' ? null : trimmed;
  })
  .superRefine((value, ctx) => {
    const problem = releaseDateProblem(value);
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
  });

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * A release date as the page says it.
 *
 * The note wins when there is no date, which is how "When it is ready" gets onto a page with no
 * day to put there. With both, the date leads and the note is the detail beside it.
 */
export function formatReleaseDate(
  date: string | null | undefined,
  note?: string | null,
): string | null {
  const said = (note ?? '').trim();
  if (!date) return said || null;
  if (!RELEASE_DATE_RULE.test(date)) return said || date;

  const [year, month, day] = date.split('-');
  const monthName = month ? MONTHS[Number(month) - 1] : undefined;
  const written = day
    ? `${String(Number(day))} ${monthName ?? ''} ${year ?? ''}`.replace(/\s+/g, ' ').trim()
    : monthName
      ? `${monthName} ${year ?? ''}`.trim()
      : (year ?? date);

  return said ? `${written} — ${said}` : written;
}
